import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RestaurantProfile } from './steps/RestaurantProfile';
import { POSConnection } from './steps/POSConnection';
import { LoyaltyConnection } from './steps/LoyaltyConnection';
import { SocialConnection } from './steps/SocialConnection';
import { ReviewPlatformSetup } from './steps/ReviewPlatformSetup';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './OnboardingWizard.css';

export interface OnboardingData {
  // Step 1 - Restaurant Profile
  restaurantName: string;
  cuisine: string;
  address: string;
  ownerName: string;
  ownerEmail: string;
  brandVoice: {
    tone: string;
    style: string;
    signOff: string;
  };
  // Step 2 - POS/Ordering Connection
  posType: string;
  posCredentials: {
    clientId: string;
    clientSecret: string;
  };
  restaurantExternalId: string;
  // Step 3 - Loyalty Platform
  loyaltyPlatform: string;
  loyaltyCredentials: {
    apiKey: string;
    integrationId: string;
  };
  // Step 4 - Social Connections
  socialConnections: {
    facebook: boolean;
    instagram: boolean;
    tiktok: boolean;
    twitter: boolean;
    google: boolean;
    yelp: boolean;
    yelpUrl: string;
  };
  // Step 5 - Review Platforms
  googleConnected: boolean;
  yelpUrl: string;
}

interface OnboardingWizardProps {
  storeId?: string;
  onComplete?: (data: OnboardingData) => void;
}

interface StoreOption {
  storeId: string;
  storeName: string;
}

const STEPS = [
  { number: 1, label: 'Restaurant Profile', icon: 'fas fa-utensils' },
  { number: 2, label: 'POS / Ordering', icon: 'fas fa-cash-register' },
  { number: 3, label: 'Loyalty', icon: 'fas fa-gift' },
  { number: 4, label: 'Social Media', icon: 'fas fa-share-alt' },
  { number: 5, label: 'Reviews / Summary', icon: 'fas fa-star' },
];

const INITIAL_DATA: OnboardingData = {
  restaurantName: '',
  cuisine: '',
  address: '',
  ownerName: '',
  ownerEmail: '',
  brandVoice: {
    tone: 'warm and professional',
    style: 'conversational yet respectful',
    signOff: 'the management team',
  },
  posType: '',
  posCredentials: {
    clientId: '',
    clientSecret: '',
  },
  restaurantExternalId: '',
  loyaltyPlatform: '',
  loyaltyCredentials: {
    apiKey: '',
    integrationId: '',
  },
  socialConnections: {
    facebook: false,
    instagram: false,
    tiktok: false,
    twitter: false,
    google: false,
    yelp: false,
    yelpUrl: '',
  },
  googleConnected: false,
  yelpUrl: '',
};

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ storeId: propStoreId, onComplete }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(1);
  const [data, setData] = useState<OnboardingData>(INITIAL_DATA);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState(propStoreId || '');
  const [storesLoading, setStoresLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState<OnboardingData | null>(null);
  const [loadingExistingData, setLoadingExistingData] = useState(false);
  const approvedFields = useRef<Set<string>>(new Set());

  const fetchExistingStoreData = useCallback(async (storeId: string) => {
    if (!storeId) return;
    setLoadingExistingData(true);
    approvedFields.current = new Set();

    try {
      const [storeResult, socialResult, reviewResult] = await Promise.allSettled([
        authenticatedFetch(API_ENDPOINTS.store(storeId)),
        authenticatedFetch(API_ENDPOINTS.socialMediaAccounts() + `?storeId=${storeId}`),
        authenticatedFetch(API_ENDPOINTS.reviewConfig(storeId)),
      ]);

      const merged: OnboardingData = JSON.parse(JSON.stringify(INITIAL_DATA));

      // Merge store profile data
      if (storeResult.status === 'fulfilled' && storeResult.value.ok) {
        const store = await storeResult.value.json();
        const s = store.data || store;
        if (s.storeName) merged.restaurantName = s.storeName;
        if (s.cuisine) merged.cuisine = s.cuisine;
        if (s.address) merged.address = s.address;
        if (s.ownerName) merged.ownerName = s.ownerName;
        if (s.ownerEmail) merged.ownerEmail = s.ownerEmail;
        if (s.posType) merged.posType = s.posType;
        if (s.restaurantExternalId) merged.restaurantExternalId = s.restaurantExternalId;
      }

      // Merge social media connections
      if (socialResult.status === 'fulfilled' && socialResult.value.ok) {
        const socialData = await socialResult.value.json();
        const accounts = Array.isArray(socialData) ? socialData : (socialData.data || socialData.accounts || []);
        accounts.forEach((acct: any) => {
          const platform = (acct.platform || '').toLowerCase();
          if (platform in merged.socialConnections && platform !== 'yelpUrl') {
            (merged.socialConnections as any)[platform] = true;
          }
        });
      }

      // Merge review config
      if (reviewResult.status === 'fulfilled' && reviewResult.value.ok) {
        const config = await reviewResult.value.json();
        const c = config.data || config;
        if (c.brandVoiceTone) merged.brandVoice.tone = c.brandVoiceTone;
        if (c.brandVoiceStyle) merged.brandVoice.style = c.brandVoiceStyle;
        if (c.signOff) merged.brandVoice.signOff = c.signOff;
        if (c.googleConnected) merged.googleConnected = true;
        if (c.yelpBusinessUrl) {
          merged.yelpUrl = c.yelpBusinessUrl;
          merged.socialConnections.yelpUrl = c.yelpBusinessUrl;
          merged.socialConnections.yelp = true;
        }
      }

      setData(merged);
      setOriginalData(JSON.parse(JSON.stringify(merged)));
    } catch (err) {
      console.error('Failed to fetch existing store data:', err);
    } finally {
      setLoadingExistingData(false);
    }
  }, []);

  useEffect(() => {
    if (propStoreId) {
      setSelectedStoreId(propStoreId);
      setStoresLoading(false);
      fetchExistingStoreData(propStoreId);
      return;
    }
    fetchStores();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propStoreId, fetchExistingStoreData]);

  const fetchStores = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.stores());
      if (response.ok) {
        const result = await response.json();
        const storeList = Array.isArray(result) ? result : (result.data || result.stores || []);
        const mapped = storeList.map((s: any) => ({
          storeId: s.storeId || s.id,
          storeName: s.storeName || s.name || s.storeId || 'Unnamed Store',
        }));
        setStores(mapped);
        if (mapped.length === 1) {
          setSelectedStoreId(mapped[0].storeId);
          fetchExistingStoreData(mapped[0].storeId);
        }
      }
    } catch (err) {
      console.error('Failed to fetch stores:', err);
    } finally {
      setStoresLoading(false);
    }
  };

  const handleUpdate = useCallback((updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  }, []);

  const handleGuardedUpdate = useCallback((updates: Partial<OnboardingData>) => {
    if (!originalData) {
      // New store or no existing data - pass through
      setData(prev => ({ ...prev, ...updates }));
      return;
    }

    const blockedKeys: string[] = [];
    for (const key of Object.keys(updates) as (keyof OnboardingData)[]) {
      if (approvedFields.current.has(key)) continue;
      const origVal = originalData[key];
      // Check if original had a non-empty value
      const hasOriginal = origVal !== '' && origVal !== false && origVal !== null && origVal !== undefined
        && !(typeof origVal === 'object' && !Array.isArray(origVal) && Object.values(origVal).every(v => !v));
      if (hasOriginal) {
        blockedKeys.push(key);
      }
    }

    if (blockedKeys.length > 0) {
      const confirmed = window.confirm(
        'This setting is already configured. Are you sure you want to change it?'
      );
      if (!confirmed) return;
      blockedKeys.forEach(k => approvedFields.current.add(k));
    }

    setData(prev => ({ ...prev, ...updates }));
  }, [originalData]);

  const handleNext = () => {
    if (activeStep < STEPS.length) {
      setActiveStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (activeStep < STEPS.length) {
      setActiveStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = async () => {
    if (!selectedStoreId) {
      setSaveError('Please select a store before finishing setup.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Save store profile updates
      const storeUpdate: any = {};
      if (data.restaurantName) storeUpdate.storeName = data.restaurantName;
      if (data.cuisine) storeUpdate.cuisine = data.cuisine;
      if (data.address) storeUpdate.address = data.address;
      if (data.ownerName) storeUpdate.ownerName = data.ownerName;
      if (data.ownerEmail) storeUpdate.ownerEmail = data.ownerEmail;

      if (Object.keys(storeUpdate).length > 0) {
        await authenticatedFetch(API_ENDPOINTS.store(selectedStoreId), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(storeUpdate),
        });
      }

      // Save review config (brand voice + Yelp URL)
      const reviewConfig: any = {};
      if (data.brandVoice.tone) reviewConfig.brandVoiceTone = data.brandVoice.tone;
      if (data.brandVoice.style) reviewConfig.brandVoiceStyle = data.brandVoice.style;
      if (data.brandVoice.signOff) reviewConfig.signOff = data.brandVoice.signOff;
      if (data.yelpUrl) reviewConfig.yelpBusinessUrl = data.yelpUrl;
      if (data.googleConnected) reviewConfig.googleConnected = true;

      if (Object.keys(reviewConfig).length > 0) {
        await authenticatedFetch(API_ENDPOINTS.reviewConfig(selectedStoreId), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(reviewConfig),
        });
      }

      setSaveSuccess(true);

      if (onComplete) {
        onComplete(data);
      }

      // Navigate to social media dashboard after a brief pause
      setTimeout(() => {
        navigate('/social-media');
      }, 2000);
    } catch (err) {
      console.error('Failed to save onboarding data:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(data.restaurantName.trim() && data.cuisine && data.ownerEmail.trim());
      case 2:
        return true; // POS is optional
      case 3:
        return true; // Loyalty is optional/skippable
      case 4:
        return true; // Social is optional/skippable
      case 5:
        return true; // Reviews/summary
      default:
        return false;
    }
  };

  const isStepConfigured = (stepNumber: number): boolean => {
    if (!originalData) return false;
    switch (stepNumber) {
      case 1:
        return !!(originalData.restaurantName || originalData.cuisine || originalData.ownerEmail);
      case 2:
        return !!(originalData.posType || originalData.restaurantExternalId);
      case 3:
        return !!(originalData.loyaltyPlatform);
      case 4: {
        const sc = originalData.socialConnections;
        return !!(sc.facebook || sc.instagram || sc.tiktok || sc.twitter || sc.google || sc.yelp);
      }
      case 5:
        return !!(originalData.googleConnected || originalData.yelpUrl);
      default:
        return false;
    }
  };

  const getStepStatus = (stepNumber: number): 'active' | 'completed' | 'pending' | 'configured' => {
    if (stepNumber === activeStep) return 'active';
    if (stepNumber < activeStep) return 'completed';
    if (isStepConfigured(stepNumber)) return 'configured';
    return 'pending';
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return <RestaurantProfile data={data} onUpdate={handleGuardedUpdate} />;
      case 2:
        return <POSConnection data={data} onUpdate={handleGuardedUpdate} storeId={selectedStoreId} />;
      case 3:
        return <LoyaltyConnection data={data} onUpdate={handleGuardedUpdate} storeId={selectedStoreId} />;
      case 4:
        return <SocialConnection data={data} onUpdate={handleGuardedUpdate} storeId={selectedStoreId} />;
      case 5:
        return <ReviewPlatformSetup data={data} onUpdate={handleGuardedUpdate} storeId={selectedStoreId} />;
      default:
        return null;
    }
  };

  if (saveSuccess) {
    return (
      <div className="onboarding-wizard">
        <div className="wizard-step" style={{ textAlign: 'center', padding: '60px 30px' }}>
          <i className="fas fa-check-circle" style={{ fontSize: 48, color: '#22c55e', marginBottom: 16 }}></i>
          <h2>Setup Complete</h2>
          <p style={{ color: '#666', marginTop: 8 }}>
            Your store has been configured. Redirecting to Social Media dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-wizard">
      {saving && (
        <div className="wizard-saving-overlay">
          <div className="wizard-saving-content">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Saving your configuration...</p>
          </div>
        </div>
      )}

      <div className="onboarding-wizard-header">
        <h1>{originalData ? 'Update Store Configuration' : 'Store Onboarding'}</h1>
        <p>{originalData ? 'Review and update your existing configuration' : 'Set up your restaurant in a few quick steps'}</p>
      </div>

      {/* Store Selector - show if no propStoreId and multiple stores */}
      {!propStoreId && stores.length > 1 && (
        <div className="wizard-store-selector">
          <label>Select Store:</label>
          <select
            value={selectedStoreId}
            onChange={(e) => {
              const newStoreId = e.target.value;
              setSelectedStoreId(newStoreId);
              setData(INITIAL_DATA);
              setOriginalData(null);
              approvedFields.current = new Set();
              setActiveStep(1);
              if (newStoreId) {
                fetchExistingStoreData(newStoreId);
              }
            }}
          >
            <option value="">-- Select a store --</option>
            {stores.map(s => (
              <option key={s.storeId} value={s.storeId}>{s.storeName}</option>
            ))}
          </select>
        </div>
      )}

      {storesLoading ? (
        <div className="wizard-step" style={{ textAlign: 'center', padding: '40px' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, color: '#4a6cf7' }}></i>
          <p style={{ color: '#666', marginTop: 12 }}>Loading stores...</p>
        </div>
      ) : loadingExistingData ? (
        <div className="wizard-loading-existing">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading existing configuration...</p>
        </div>
      ) : (
        <>
          {/* Progress Bar */}
          <div className="wizard-progress">
            {STEPS.map((step, index) => {
              const status = getStepStatus(step.number);
              return (
                <React.Fragment key={step.number}>
                  <div className="wizard-progress-step">
                    <div className={`step-indicator ${status}`}>
                      {status === 'completed' ? (
                        <i className="fas fa-check"></i>
                      ) : status === 'configured' ? (
                        <i className="fas fa-check-circle"></i>
                      ) : (
                        step.number
                      )}
                    </div>
                    <span className={`step-label ${status}`}>{step.label}</span>
                    {status === 'configured' && (
                      <span className="step-configured-badge">Configured</span>
                    )}
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`wizard-progress-connector ${
                        step.number < activeStep ? 'completed' : ''
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {originalData && (
            <div className="wizard-existing-data-banner">
              <i className="fas fa-info-circle"></i>
              <span>Existing configuration has been loaded. You will be prompted before overwriting any configured settings.</span>
            </div>
          )}

          {saveError && (
            <div className="wizard-alert wizard-alert-error">
              <i className="fas fa-exclamation-circle"></i>
              <span>{saveError}</span>
            </div>
          )}

          {/* Step Content */}
          <div className="wizard-step">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="wizard-navigation">
            <div className="wizard-nav-left">
              {activeStep > 1 && (
                <button className="wizard-btn wizard-btn-secondary" onClick={handleBack}>
                  <i className="fas fa-arrow-left"></i>
                  Back
                </button>
              )}
            </div>
            <div className="wizard-nav-right">
              {activeStep > 1 && activeStep < STEPS.length && (
                <button className="wizard-btn wizard-btn-skip" onClick={handleSkip}>
                  Skip this step
                </button>
              )}
              {activeStep < STEPS.length ? (
                <button
                  className="wizard-btn wizard-btn-primary"
                  onClick={handleNext}
                  disabled={!isStepValid(activeStep)}
                >
                  Next
                  <i className="fas fa-arrow-right"></i>
                </button>
              ) : (
                <button
                  className="wizard-btn wizard-btn-success"
                  onClick={handleFinish}
                  disabled={saving || !selectedStoreId}
                >
                  {saving ? 'Saving...' : 'Finish Setup'}
                  {!saving && <i className="fas fa-check"></i>}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnboardingWizard;
